import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import type { AdminAnnouncement, AdminAnnouncementPush, AdminAudience } from '@/api/types';

export const announcementKeys = {
  all: ['announcements'] as const,
  list: (params: Record<string, string | number | undefined>) =>
    [...announcementKeys.all, 'list', params] as const,
  dismissals: (id: string) =>
    [...announcementKeys.all, 'dismissals', id] as const,
};

export function useAnnouncements(params: { status?: string; limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: announcementKeys.list(params),
    queryFn: () => adminClient.listAnnouncements(params),
  });
}

export function useAnnouncementDismissals(id: string) {
  return useQuery({
    queryKey: announcementKeys.dismissals(id),
    queryFn: () => adminClient.getAnnouncementDismissals(id),
    enabled: !!id,
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Omit<AdminAnnouncement, 'id' | 'createdAt' | 'updatedAt'> & {
        audience?: AdminAudience;
        push?: AdminAnnouncementPush;
      },
    ) => adminClient.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Omit<AdminAnnouncement, 'id' | 'createdAt'>> & { audience?: AdminAudience }) =>
      adminClient.updateAnnouncement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminClient.deleteAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}
